type ConvertStringLiteralsToStringDeep<T> = T extends string
  ? string
  : {
      [K in keyof T]: T[K] extends infer U ? (U extends object ? ConvertStringLiteralsToStringDeep<U> : string) : never;
    };

export const messages = {
  action: {
    account: 'My ATLAS.ti',
    addCode: 'Add code',
    addCodeToCategory: 'Add code to category',
    addCodeToFolder: 'Add code to folder',
    addCodeToGroup: 'Add code to group',
    addComment: 'Add a comment',
    addFilter: 'Add filter',
    addProject: 'Add project',
    addQuotation: 'Select quotation',
    addSource: 'Add document',
    addToGroup: 'Add to group',
    aiCoding: 'AI Coding (+ Intention)',
    aiSummary: 'AI Summary',
    apply: 'Apply',
    back: 'Back',
    cancel: 'Cancel',
    close: 'Close',
    codeResults: 'Quote nothing | Quote 1 result | Quote {count} results',
    contactSupport: 'Contact Support',
    continue: 'Continue',
    conversationalAi: 'Conversational AI',
    copy: 'Copy to clipboard',
    createCode: 'New Code',
    createCodeAlt: 'You can create a new code by pressing {shortcut}.',
    createCodeGroup: 'Create code group',
    createCodeInCategory: 'Create code in category',
    createCodeInFolder: 'Create code in folder',
    createCodeInGroup: 'Create code in group',
    createdBy: 'Created by',
    createInVivoCode: 'Create in-vivo code',
    createInVivoCodeInCategory: 'Create in-vivo code in category',
    createInVivoCodeInFolder: 'Create in-vivo code in folder',
    createInVivoCodeInGroup: 'Create in-vivo code in group',
    createMemo: 'New memo',
    createMemoAlt: 'You can create a new memo by pressing {shortcut}.',
    createNewProject: 'Create Project',
    createProject: 'Create project',
    createProjectAlt: 'You can create a new project by pressing {shortcut}.',
    createSource: 'Create document',
    createSourceAlt: 'You can create a new document by pressing {shortcut}.',
    createSourceTitle: 'New Document',
    createView: 'New filter View',
    delete: 'Delete',
    deleteCode: 'Delete code',
    deleteDocument: 'Delete document',
    deleteMemo: 'Delete memo',
    deleteQuotation: 'Delete quotation',
    done: 'Done',
    dontShowAgain: "Don't show again",
    download: 'Download',
    duplicate: 'Duplicate',
    edit: 'Edit',
    editCodeGroups: 'Edit code groups',
    editGroups: 'Edit groups',
    editMemoGroups: 'Edit memo groups',
    editSourceGroups: 'Edit document groups',
    filter: 'Filter',
    goToCode: 'Go to code',
    goToDocument: 'Go to document',
    goToMemo: 'Go to memo',
    goToProjectTemplateImport: 'Go to project template import',
    hide: 'Hide',
    hideComments: 'Hide comments',
    importProject: 'Import Desktop',
    importProjectA9: 'Import ATLAS.ti 9+ Project',
    importSurveyData: 'Import survey data',
    invite: 'Invite',
    learnMore: 'Learn more',
    logout: 'Logout',
    makeSubcodeOf: 'Make Subcode of {name}',
    manageCodes: 'Manage Codes',
    manageGroups: 'Manage Groups',
    mergeCodes: 'Merge codes',
    moveCodes: 'Move code | Move {count} codes',
    next: 'Next',
    open: 'Open',
    openInManager: 'Open in manager',
    proceed: 'Proceed',
    reloadPage: 'Reload page',
    renameCode: 'Rename code',
    renameMemo: 'Rename memo',
    renameSource: 'Rename document',
    reset: 'Reset',
    retry: 'Retry',
    save: 'Save',
    search: 'Search',
    searchName: 'Search name',
    searchText: 'Search text',
    send: 'Send',
    showAll: 'Show all',
    showAllQuotations: 'Show all quotations',
    showComments: 'Show comments',
    showDetails: 'Show all details',
    showLess: 'Show less',
    showSourceInfo: 'Show document info',
    skip: 'Skip',
    sortAscending: 'Sort ascending',
    sortBy: 'Sort by:',
    sortDescending: 'Sort descending',
    startSearch: 'Start Search',
    syncMemo: 'Sync Memo',
    syncSource: 'Sync Document',
    toggleComment: 'Toggle comment',
    toggleComments: 'Toggle comments',
    toProjectOverview: 'All Projects',
    undo: 'Undo',
    upload: 'Upload',
    uploadPdf: 'Import PDF document',
    uploadWord: 'Import Word document',
    wrapColumn: 'Wrap column',
  },
  aiCoding: {
    activeJobs: {
      button: 'See progress',
      message: 'You have an ongoing AI Coding process',
    },
    consent: {
      checkbox:
        'I have read and agree to the <a href="https://atlasti.com/legal/terms-conditions" target="_blank"><u>License Agreement</u></a> and the <a href="https://atlasti.com/legal/privacy-policy/" target="_blank"><u>Privacy Policy</u></a>.',
      description:
        'By utilizing this AI functionality, your data is transmitted to OpenAI for processing in strict adherence to our Privacy Policy.',
      title: 'Privacy Notice',
    },
    error: 'An error occurred: {code}',
    errors: {
      AI_CODING_IN_PROGRESS: {
        description: 'An AI Coding process is already in progress.',
      },
      CORE_AI_COMPANY_LIMIT: {
        description:
          'AI Coding is currently experiencing high load.<br /><br />Please wait a few minutes and try again.',
      },
      CORE_AI_LICENSE_LIMIT: {
        description:
          'Executing this operation will exceed the AI usage limit for your license.<br /><br />Please, try again with a smaller document or contact your license administrator (or ATLAS.ti Support) to increase the limit.',
        title: "You have reached your license's AI usage limit",
      },
      CORE_AI_TRIAL_LIMIT: {
        description:
          'Please try again with a smaller document.<br /><br />To enable AI Coding without restrictions, visit our shop to purchase a license.',
        title: 'You have reached the Trial limit',
      },
      CORE_AI_UNSPECIFIED_LIMIT: {
        description:
          'This is an experimental feature. Please try again later.<br /><br />If the problem persists and you need to use AI Coding, please contact support.',
      },
      CORE_AI_USER_LIMIT: {
        description:
          'AI functionality has been automatically suspended for your account due to unusually high usage.<br /><br />To reinstate AI features, please contact support and describe your use case.',
      },
      DEFAULT: {
        description: 'An error ocurred during AI Coding:<br /><br />"{message}"',
      },
      title: 'AI Coding Failed',
    },
    info: {
      description:
        'Say goodbye to endless manual coding and hello to AI-driven assistance on autopilot – powered by OpenAi.',
      dontShowAgain: "Don't show this again",
      featured: {
        0: {
          description:
            'Fully automated code proposals take the grunt work out of your hands, so you can focus on refinement and analysis.',
          title: '10X Analysis Speed',
        },
        1: {
          description:
            'AI-assisted coding can help you reduce human error and increase the reliability of the analysis.',
          title: 'Improved Accuracy',
        },
        2: {
          description:
            'Uncover deeper insights and patterns in your data that may have been missed using manual coding methods.',
          title: 'Improved Insights',
        },
      },
      openDocuments: 'Open documents',
      steps: {
        1: 'Select one or more documents',
        2: 'Click “AI Coding”',
        3: 'Follow the instructions ;)',
      },
      subtitle: 'Powered by OpenAI',
      tryNow: 'Try it now',
    },
    loader: {
      estimate: 'Estimated Time: {estimate}',
      text: 'Depending on the workload, OpenAI may experience strong performance variations, often resulting in very long waiting times. ATLAS.ti has no influence on this.',
      title: 'AI Coding in Progress',
    },
    recentlyCompletedJobs: {
      button: 'Review results',
      message: 'You have recently completed AI Coding processes',
    },
    relatedQuestions: {
      backTooltip:
        'Go back to edit Research Goal.<br>Your changes to related questions and code categories will be deleted when restarting<br>the process.',
      info: {
        description: `ATLAS.ti could not generate any questions tailored to your initial research goal. Please, add custom questions below. These questions will be used to extract relevant insights from your data in the next step. They must be assigned a category code to further improve AI Coding results. Think of categories as a “theme” or “topic” that groups together several more specific codes. For further details on the related question and category code, explore our Help Center.
          | ATLAS.ti has generated 1 question tailored to your initial research goal. This question will be used to extract relevant insights from your data in the next step. The question is assigned a category code that will further improve AI Coding results. Think of categories as a “theme” or “topic” that groups together several more specific codes. For further details on the related question and category code, explore our Help Center.
          | ATLAS.ti has generated {count} questions tailored to your initial research goal. These questions will be used to extract relevant insights from your data in the next step. Each question is assigned a category code which will further improve AI Coding results. Think of categories as a “theme” or “topic” that groups together several more specific codes. For further details on the related questions and category codes, explore our Help Center.`,
        link: 'https://atlastihelp.helpscoutdocs.com/article/729-how-to-use-ai-coding-in-atlas-ti',
        title: 'What is happening here?',
      },
      list: {
        addCustomQuestion: 'Add Custom Question',
        header: {
          codedWith: 'Category for AI codes',
          selectedQuestionsCounter: '1 question | {count} questions',
        },
        item: {
          addCode: 'Add Code',
        },
      },
      loading: {
        description:
          'Based on your research goal, ATLAS.ti will generate related questions that will help AI Coding to improve results.',
        note: 'This process can take up to 30 seconds',
        title: 'Generating Questions',
      },
      nextTooltip: 'To proceed, make sure all selected questions are not empty and assigned to a category code',
    },
    reportName: 'AI Coding Summary {date}',
    researchGoal: {
      description: 'AI Coding can be improved when you first define a clear and concise research goal.',
      editor: {
        charCounter: '{numChars} of 1600 characters',
        placeholder: 'Your research goal goes here…',
      },
      examples: {
        1: 'Explore the relationship between social media usage and anxiety levels among college students',
        2: 'Assess the impact of plastic waste on marine ecosystems in the Gulf of Mexico',
        3: 'Evaluate the effectiveness of plant-based diets in reducing the risk of heart disease',
        title: 'Examples of Research Goals:',
      },
      info: {
        description:
          'A research goal is a clear statement detailing the objective of your study, specifying what you plan to investigate, analyze, or comprehend.<br />Setting a Research Goal is recommended as it will lead to more accurate results during AI Coding.',
        link: 'https://atlastihelp.helpscoutdocs.com/article/729-how-to-use-ai-coding-in-atlas-ti',
        title: 'What is a Research Goal?',
      },
    },
    review: {
      applyButton: 'Apply AI Coding',
      applyTooltip: {
        applyCodes: 'Applies 1 existing code | Applies {count} existing codes',
        createCodes: 'Creates 1 code | Creates {count} codes',
        createQuotations: 'Creates 1 quotation | Creates {count} quotations',
        noneSelected: 'No new codes or quotations selected',
        updateQuotations: 'Updates 1 quotation | Updates {count} quotations',
      },
      backTooltip:
        'Go back to edit related questions and code categories.<br />Your changes to quotations or codes will be deleted when restarting the process.',
      codeList: {
        codeGeneratedByAi: 'This code was generated by AI Coding',
        codeOccurrenceTooltip:
          'This code will be applied to 1 quotation | This code will be applied to {count} quotations',
        noResults: {
          message: 'No suitable codes found or found codes are already existing',
          title: 'No new codes found',
        },
        topBar: {
          title: '0 Suggested codes | 1 Suggested code | {count} Suggested codes',
        },
      },
      noResults: {
        message: 'No new codes or quotations have been found for this document.',
        title: 'Sorry :-(',
      },
      notification: {
        error: 'An error occurred while adding quotations: {message}',
        progress: '{count} quotations are being added',
        success: '{count} quotations have been added',
        successAction: 'Go to summary',
      },
      quotationList: {
        noResults: {
          message: 'No suitable quotations found or found quotations already exist',
          title: 'No new quotations found',
        },
        topBar: {
          selectedQuotationCounter: '0 quotations | 1 quotation | {count} quotations',
        },
      },
      title: 'Review the results',
    },
    savedProgress: 'Your progress has been saved, AI Coding will take it from there on the next run.',
    skipTooltip: 'Continue AI Coding without Research Goal<br>(Not recommended)',
    steps: {
      2: 'Define Research Goal',
      3: 'Related Questions and Category Codes',
      4: 'Review & Apply Results',
      5: 'Summary',
    },
    summary: {
      cancel: {
        confirm: 'Cancel AI Coding',
        message: 'Are you sure you want to cancel this process?',
        success: 'AI Coding canceled',
        title: 'Before you cancel…',
      },
      createdCodes: 'Created Codes',
      createdQuotations: 'Created Quotations',
      title: 'AI Coding Summary',
    },
    title: 'AI Coding',
  },
  aiConsent:
    'I have read and agree to the <a href="https://atlasti.com/legal/terms-conditions" target="_blank">License Agreement</a> and the <a href="https://atlasti.com/legal/privacy-policy/" target="_blank">Privacy Policy</a>.',
  aiDisabledModal: {
    description:
      'Please note that the AI features have been disabled for this account by the license owner or administrator. For more information or assistance, please contact your license administrator or ATLAS.ti Support.',
    title: 'AI features disabled by administrator',
  },
  aiExperimentalInfo: {
    badge: {
      label: 'Experimental',
    },
    modal: {
      cta: {
        cancel: 'Cancel',
        sendFeedback: 'Send us your feedback',
      },
      message:
        'Our new AI features are experimental and may behave unexpectedly. Your feedback is invaluable for improvements. Please proceed with understanding and caution. Thank you for your continued support.',
      title: 'Experimental Features',
    },
  },
  aiSplashScreen: {
    featured: {
      0: {
        description: '<a href="https://atlasti.com/conversational-ai" target="_blank">Learn more</a>',
        title: 'Chat and interact with your documents',
      },
      1: {
        description: '<a href="https://atlasti.com/ai-coding-powered-by-openai" target="_blank">Learn more</a>',
        title: 'AI-Enhanced Coding for Swift Data Analysis',
      },
      2: {
        description: '<a href="https://atlasti.com/suggested-codes-in-atlas-ti-web" target="_blank">Learn more</a>',
        title: 'AI-Powered Code Suggestions',
      },
    },
    subtitle:
      "Transform Your Qualitative Analysis: Leverage OpenAI's Advanced AI for Deeper Insights and Efficient Data Processing with ATLAS.ti",
    title: 'Discover Faster, Smarter Research with ATLAS.ti Web: <i>Powered by OpenAI</i> for Enhanced Data Analysis',
  },
  aiTools: 'AI Tools',
  authorInfo: {
    code: {
      created: 'Code created {timeAgo}',
      createdBy: 'Code created {timeAgo} by {name}',
    },
    document: {
      created: 'Document created {timeAgo}',
      createdBy: 'Document created {timeAgo} by {name}',
    },
    memo: {
      created: 'Memo created {timeAgo}',
      createdBy: 'Memo created {timeAgo} by {name}',
    },
    quotation: {
      created: 'Quotation created {timeAgo}',
      createdBy: 'Quotation created {timeAgo} by {name}',
    },
    report: {
      created: 'View created {timeAgo}',
      createdBy: 'View created {timeAgo} by {name}',
    },
    updated: 'Updated {timeAgo}',
    updatedBy: 'Updated {timeAgo} by {name}',
  },
  baseOverview: {
    emptyFilter: {
      change: 'Change the filter rules',
      reset: 'reset the rules',
      text: '{change} or {reset}.',
      title: 'No results for your search',
    },
  },
  batchSelection: {
    actions: {
      delete: 'Delete',
      duplicate: 'Duplicate',
      manageGroups: 'Manage Groups',
    },
    description: 'Shift+Click to select, esc to deselect all',
    info: '<strong>Shift+Click</strong> to select, <strong>esc</strong> to deselect all',
  },
  beta: 'Beta',
  by: 'by',
  categoryCodeSelect: {
    item: {
      subCodesTitle: 'This category code has {count} subcode(s).',
    },
    rootLevelCode: 'Root Level (no parent code)',
    tooltip:
      "Organize codes under a parent code for better hierarchy. Once used for coding, a code can't become a parent anymore since Parent codes are only for structuring, not for document coding.",
  },
  categoryCodeUpsert: {
    movedCodes: 'Moved "{name}" under "{parentName}". | Moved {count} codes under "{parentName}".',
    removedCodes: 'Removed "{name}" from category code. | Removed {count} codes from category code.',
    removedCodesNameChange:
      'Changed "{name}" to "{newName}" to avoid duplicate names. | Renamed {count} codes to avoid duplicate names.',
  },
  codeFloat: {
    aiCodeSuggestions: {
      consent: {
        buttons: {
          cancel: 'Cancel',
          confirm: 'Proceed',
        },
        cta: {
          message:
            'I have read and agree to the <a href="https://atlasti.com/legal/terms-conditions" target="_blank">License Agreement</a> and the <a href="https://atlasti.com/legal/privacy-policy/" target="_blank">Privacy Policy</a>.',
        },
        message: 'The text content and created codes will be uploaded to OpenAI for processing.',
      },
      errors: {
        default: {
          message: 'An Error occurred. Please try again later.',
        },
        server: {
          code: '50x',
          message: 'A Server Error occurred. Please try again later.',
        },
      },
      noResults: {
        message: 'No AI Code Suggestions found.',
      },
      title: 'Suggested Codes',
    },
    commentPlaceholder: 'Add comment to quotation',
    createInVivoQuotation: 'In-vivo',
    createInVivoQuotationTitle: 'Create quote with in-vivo code "{name}"',
    createNewCode: 'Create new code',
    createQuotation: 'Quote',
    error: {
      codeReference: 'Error code: {code}.',
      create: 'Could not create quotation.',
      update: 'Could not update quotation.',
    },
    updateQuotation: 'Update',
  },
  codeOverview: {
    deleted: 'No Codes deleted | 1 Code deleted | {count} Codes deleted',
    dropActions: {
      categoryCode: {
        error: {
          inUse: 'Codes that are already in use cannot become category codes',
          isCategory: 'Category codes cannot become subcodes',
          sameCode: 'Code cannot become its own subcode',
          sameParent: 'Codes are already subcodes of this category code',
        },
      },
      mergeCodes: {
        error: {
          categoryAndSubcode: 'Cannot merge category codes and subcodes',
          inDifferentCategoryCodes: 'Cannot merge subcodes in different category codes',
          multipleCategoryCodes: 'Cannot merge category codes',
        },
      },
    },
    duplicated: 'No Codes duplicated | 1 Code duplicated | {count} Codes duplicated',
    empty: {
      cta: 'Create code',
      helpUrl: 'https://atlastihelp.helpscoutdocs.com/article/745-what-is-the-code-manager',
      icon: '👀',
      text: 'Codes (or tags) help you identify patterns in your documents and make it easy to understand and evaluate complex issues. {link}',
      title: 'Use Codes to tag your most relevant text passages.',
    },
    exportCSV: 'Download Codes as CSV',
    exportCSVFiltered: 'Download filtered codes as CSV',
    exportXLSX: 'Download Codes as Excel',
    exportXLSXFiltered: 'Download filtered codes as Excel',
    exportXML: 'Download Codes as QDC',
    exportXMLFiltered: 'Download filtered codes as QDC',
    moveCodes: {
      error: 'Could not move code {name} under {parentName}: {error}',
      moveToRoot: 'Drop here to move to root level',
    },
    selected: 'No Codes selected | 1 Code selected | {count} Codes selected',
    title: 'Code Manager',
  },
  codePill: {
    attached: 'Attached {timeAgo} by {name}',
  },
  codePillList: {
    emptyPlaceholder: {
      quotation: 'This quotation has no codes.',
      source: 'This document has no codes.',
    },
  },
  codeSelect: {
    searchPlaceholders: {
      code: 'Find or create codes',
      codeCategory: 'Find category codes',
      codeFolder: 'Find or create code folders',
      codeGroup: 'Find or create code groups',
      codeInCategory: 'Find or create subcodes',
      codeInFolder: 'Find or create codes in folder',
      codeInGroup: 'Find or create codes in group',
    },
    sectionTitles: {
      existingCodes: 'Existing codes',
      newCodes: 'New codes',
      recentCodes: 'Recent codes',
    },
  },
  colorPicker: {
    chooseColor: 'Choose color',
    noColor: 'No color',
  },
  confirmAccount: {
    cta: 'Resend confirmation e-mail',
    notice: '<strong>Please confirm your e-mail address</strong> to continue using ATLAS.ti Web.',
    sent: 'We have sent a new confirmation e-mail to {email}. If you are still having trouble, please contact our support.',
  },
  confirmRouteChangeModal: {
    leave: 'Leave',
    stay: 'Stay on this page',
    text: 'You have unsaved changes. If you leave this page, they will be lost.',
    title: 'Unsaved changes',
  },
  conversationalAi: {
    consent: {
      message:
        'The text content of the selected document(s) will be uploaded to OpenAI for processing. After the chat, OpenAI will forget your data and will not save it or use it.',
      start: 'Start Conversational AI',
    },
    documentSelector: {
      currentDocument: 'Current Document',
      emptyDocument: 'Empty',
      emptyDocumentTooltip:
        'This document has no text. Conversational AI can only be used with documents that contain text.',
      noResults: {
        headline: "We couldn't find any results matching your search term.",
        reset: 'Reset',
      },
      otherDocuments: 'Other Documents',
      searchPlaceholder: 'Find document',
    },
    errorModal: {
      experimentalMessage:
        'Conversational AI is currently in its experimental phase.<br />There may be occasional errors or inaccurate results.<br />We appreciate your understanding during this phase.',
      tryAgain:
        'Please try your request again in a few minutes. If the issue persists, start a new Conversational AI session.',
    },
    errors: {
      CONVERSATIONAL_AI_SESSION_EXPIRED: {
        description:
          'Your Conversational AI session has expired due to a period of inactivity.<br />If you wish to continue, please start a new session.<br/ ><br />Please be aware that upon initiating a new session, the current chat conversation history will be lost.',
        title: 'Conversational AI Session Expired',
      },
      CORE_AI_COMPANY_LIMIT: {
        description: 'Conversational AI is quite popular at the moment and is experiencing a high load.',
      },
      CORE_AI_COUNTRY_BANNED: {
        description:
          'Unfortunately, AI services are not accessible from your location.<br />You can check the full list of supported countries and regions <a href="https://atlasti.com/ai-coding-can-only-be-used-in-the-countries-and-regions-listed-below" target="_blank">here</a>.',
        title: 'Access Denied',
      },
      CORE_AI_TRIAL_LIMIT: {
        description:
          'You reached the trial limit for AI features.<br />To continue using Conversational AI without restrictions, please visit our shop and purchase a license.',
        title: 'Trial Limit Reached',
      },
      CORE_AI_UNSPECIFIED_LIMIT: {
        description: 'Our experimental Conversational AI feature encountered an issue.',
      },
      CORE_AI_USER_LIMIT: {
        description:
          'AI features have been automatically suspended for your account due to unusually high usage.<br />To reinstate AI features, please contact support and describe your use case.',
      },
      CORE_TIMEOUT: {
        description: 'Conversational AI took too long to respond.',
      },
      DEFAULT: {
        description: 'An unexpected error ocurred',
        title: 'Conversational AI hiccup',
      },
      NO_ACCESS: {
        DEFAULT: {
          description: 'You have no access to this feature:<br />"{message}"',
          title: 'Access Denied',
        },
      },
    },
    firstMessage:
      'What do you want to know about the document: {documentNames}? | What do you want to know about the documents: {documentNames}?',
    greeting:
      'Hello {name}!<br /><br />I am a conversational AI designed to let you chat with your documents.<br /><br />Select one or more documents to start the conversation:',
    hide: 'Hide Conversational AI',
    misleading:
      'AI responses can be inaccurate or misleading. <a href="https://atlastihelp.helpscoutdocs.com/article/758-understanding-ai-assistance-in-atlas-ti" target="_blank">Learn more</a>',
    prepareDataDisclaimer: 'It can take a few minutes, please bear with us...',
    resetSession: {
      button: 'Start new session',
      modal: {
        description:
          "Closing the current session will erase the chat history, and the Conversational AI will not retain any memory of this conversation or be able to reference past messages. Select 'Cancel' to continue chatting, or 'Start new session' to proceed with closing the session.",
        title: 'Start new session',
      },
    },
    startButton: {
      loading: 'Preparing',
      startChat: 'Start with chat',
      tooltip: {
        allDocumentsEmpty: 'None of the documents in this project contain text',
        noDocumentSelected: 'To continue, please select at least one document',
        tosNotAccepted: 'To continue, please accept the License Agreement and Privacy Policy',
      },
    },
    title: 'Conversational AI',
    uploadErrors: {
      CONVERSATIONAL_AI_ALL_DOCUMENTS_EMPTY:
        "The selected documents are empty and can't be used with Conversational AI. Please select a document that contains text.",
    },
  },
  copyQuotation: {
    added: 'Added one quotation | Added {count} quotations',
    emptyState: {
      action: 'Go to Views',
      text: 'Please create a code distribution View first to select quotations.',
    },
  },
  createCodeCard: {
    cancel: 'Cancel',
    color: {
      label: 'Pick a color',
    },
    comment: {
      label: 'Comment',
      placeholder: 'Comment code',
    },
    cta: 'Create new code',
    editAiCodeHeadline: 'Edit Suggested Code',
    editButtonTitle: 'Done',
    editHeadline: 'Edit Code',
    error: {
      name: 'Please enter a name.',
      unique: 'A code with this name already exists.',
    },
    headline: 'Create a new code',
    name: {
      label: 'Name your code',
      placeholder: 'A new code',
    },
    updateButtonTitle: 'Save code',
    updateHeadline: 'Update code',
  },
  createProjectCard: {
    cancel: 'Cancel',
    color: {
      label: 'Project color',
    },
    cta: 'Create new project',
    description: {
      label: 'Project description',
      placeholder: 'Add a description',
    },
    error: {
      name: 'Please provide a name for your project.',
    },
    headline: 'Create a new project',
    label: 'Name of your project',
    placeholder: 'Cool project by {name}',
  },
  createSourceCta: {
    newTextDocumentInfo: 'Create new text document.',
    newTextDocumentTitle: 'Create Document',
    pdfUploadInfo: 'Upload size is limited to {maxSizeMb}.',
    pdfUploadTitle: 'PDF Document',
    surveyImportInfo: 'Create documents from survey data in CSV format.',
    surveyImportTitle: 'CSV Survey Data (Beta)',
    uploadInfo: {
      convertToText: 'All styles will be converted to plain text.',
      keepOriginal: 'Trying to preserve the original appearance including text styles and images.',
    },
    uploadOptions: {
      convertToText: 'Convert to text',
      keepOriginal: 'Keep original appearance',
    },
    wordUploadInfo: 'Supported file format is *.docx, text only.',
    wordUploadTitle: 'Word Document',
  },
  createSourcesCsv: {
    card: {
      error: 'Could not read CSV file.',
      loading: 'Reading CSV …',
      question: 'Question',
      step0: {
        action: 'Create documents …',
        description: 'To process your import, please select all questions from the CSV from the list below:',
        title: 'Select questions from CSV',
      },
      step1: {
        action: 'Create documents',
        description: 'Create a document for each:',
        title: 'Create documents',
      },
    },
    error: {
      general: 'Import failed – "{name}": {code}',
      input: {
        fileSize: 'File exceeds size limit of {limit} MB',
        fileType: 'Import failed – "{name}": Invalid file type',
      },
    },
    status: {
      complete: 'Created {count} documents from CSV – "{name}"',
      progress: 'Creating {count} documents from CSV – "{name}"',
    },
  },
  dateFormat: 'yyyy dd MMM ',
  deleteCode: {
    multiple: {
      deleteParentCodeAndMoveChildren:
        'Delete {deleteCount} codes and <strong>move 1 sub code to root level</strong> | Delete {deleteCount} codes and <strong>move {childCount} sub codes to root level</strong>',
      deleteParentCodeWithChildren: 'Delete {count} codes',
      descriptionBase: 'You are about to delete {count} codes.',
      descriptionWithChildren:
        'You are about to delete {deleteCount} codes, including 1 sub code. | You are about to delete {deleteCount} codes, including {childCount} sub codes.',
    },
    single: {
      deleteParentCodeAndMoveChildren:
        'Delete code and <strong>move 1 sub code to root level</strong> | Delete code and <strong>move {count} sub codes to root level</strong>',
      deleteParentCodeWithChildren: 'Delete {count} codes',
      descriptionBase: 'You are about to delete the code {name}.',
      descriptionWithChildren:
        'You are about to delete the code {name} and its 1 sub code. | You are about to delete the code {name} and its {childCount} sub codes.',
    },
  },
  deletedUser: 'Deleted User',
  dock: {
    action: {
      closeDock: 'Close sidebar',
      openInDock: 'Open in sidebar',
    },
    details: 'Details',
  },
  durationAgo: {
    days: '0 days ago | 1 day ago | {duration} days ago',
    fewSeconds: 'a few seconds ago',
    hours: '0 hours ago | 1 hour ago | {duration} hours ago',
    minutes: '0 minutes ago | 1 minute ago | {duration} minutes ago',
  },
  editCodeGroupsCard: {
    title: 'Edit Code Groups',
    tooltipText:
      'Use code groups to tag codes, e.g. group codes like <strong>Apple, Banana</strong> and <strong>Kiwi</strong> in a group named <strong>Fruit</strong>. You can then use filter rules in the Quotation Manager to show results containing this group, e.g. <strong>People talking about fruit</strong>',
  },
  editGroupsCard: {
    error: {
      new: {
        name: {
          required: 'Please provide a name for the group',
          unique: 'A group with this name already exists',
        },
      },
    },
    name: {
      createdGroups: 'Created Groups',
      createNewCodeGroup: 'Create new code group',
      createNewMemoGroup: 'Create new memo group',
      createNewSourceGroup: 'Create new document group',
      newCodeGroup: 'New code group',
      newMemoGroup: 'New memo group',
      newSourceGroup: 'New document group',
      placeholder: 'Create new group',
    },
  },
  editMemoGroupsCard: {
    title: 'Edit Memo Groups',
    tooltipText:
      'Use memo groups to tag memos, e.g. <strong>Diary, Insight</strong> or <strong>Note</strong>. You can then use filter rules to narrow down your memos by group.',
  },
  editor: {
    alignCenter: 'Align center',
    alignLeft: 'Align left',
    alignRight: 'Align right',
    backgroundColor: {
      tooltipText: 'Background color',
    },
    blockMenu: {
      copyQuotation: {
        tooltipText: 'Insert quotation (Beta)',
      },
      image: {
        tooltipText: 'Insert image',
      },
      list: {
        tooltipText: 'Insert list',
      },
    },
    bold: 'Bold',
    fontSize: {
      placeholder: 'Font Size',
      tooltipText: 'Font Size',
    },
    headline1: 'Headline 1',
    headline2: 'Headline 2',
    headline3: 'Headline 3',
    image: {
      error: {
        fileTooBig: '{name} exceeds the maximum file size of {imageMaxFileSize}.',
        fileUnsupported: 'This file type is not supported. - {name}',
      },
      uploading: 'Uploading image {name}',
    },
    italic: 'Italic',
    link: {
      placeholder: 'Add link',
      tooltipText: 'Insert link',
    },
    ol: 'Ordered List',
    strikethrough: 'Strikethrough',
    textColor: {
      tooltipText: 'Text color',
    },
    ul: 'Unordered List',
    underline: 'Underline',
  },
  editSingleGroupCard: {
    comment: {
      label: 'Comment',
      placeholder: 'Comment group',
    },
    error: {
      name: 'Please enter a name.',
      unique: 'A group with this name already exists.',
    },
    name: {
      label: 'Name',
      placeholder: 'Add a group name',
    },
    title: 'Edit {entityType} group',
  },
  editSourceGroupsCard: {
    title: 'Edit Document Groups',
    tooltipText:
      'Use document groups to tag documents, e.g. <strong>Female</strong> and <strong>Germany</strong>. You can then use filter rules in the Quotation Manager to show results containing these groups, e.g. <strong>Feedback from female respondents in Germany</strong>',
  },
  entity: {
    categoryCode: 'Category Code | Category Codes',
    code: 'Code | Codes',
    group: 'Group | Groups',
    independentCode: 'Independent Code | Independent Codes',
    memo: 'Memo | Memos',
    project: 'Project | Projects',
    quotation: 'Quotation | Quotations',
    report: 'View | Views',
    source: 'Document | Documents',
  },
  entityGroup: {
    code: 'Code Group | Code Groups',
    memo: 'Memo Group | Memo Groups',
    source: 'Document Group | Document Groups',
  },
  files: {
    error: 'Error processing {name}',
    errors: {
      convertedSize: 'Error: File is too big after conversion – {name}',
      fileSize: 'Error: File size exceeds 8 MB – {name}',
      general: 'Error: File could not be uploaded - {message} - {name}',
      notSupported: 'Error: File type is not supported – {name}',
      processing: 'Error: File could not be processed, make sure the file is a valid word document - {name}',
      upload: 'Error: File could not be uploaded. Please try again later',
    },
    processing: 'No file is being processed | Your file is being processed | {count} files are being processed',
  },
  filter: {
    add: 'Add rule',
    addFirstRule: 'Filter {entityType} by',
    and: 'and',
    button: {
      title: 'Filter',
    },
    defaults: {
      searchEmpty: 'No results for your search',
    },
    disabledButtonTooltip: 'One of your rules is missing a value.<br>Check your rules before adding a new one.',
    filters: {
      CategoryCodeFilter: {
        empty: {
          default: 'There are no category codes yet',
        },
      },
      CodeFilter: {
        empty: {
          default: 'There are no codes yet',
        },
        label: 'Code | Codes',
        placeholder: {
          default: 'codes',
        },
        searchPlaceholder: {
          default: 'Search codes',
        },
      },
      CodeFolderFilter: {
        empty: {
          default: 'There are no code folders yet',
        },
      },
      CodeGroupFilter: {
        empty: {
          default: 'There are no code groups yet',
        },
        label: 'Code group | Code groups',
        placeholder: {
          default: 'code groups',
        },
        searchPlaceholder: {
          default: 'Search code groups',
        },
      },
      CodeNameFilter: {
        label: 'Code name',
      },
      CommentFilter: {
        label: 'Comment',
      },
      CreatorFilter: {
        empty: {
          default: 'There are no users yet',
        },
        label: 'Creator | Creators',
        placeholder: {
          default: 'users',
        },
        searchPlaceholder: {
          default: 'Search users',
        },
      },
      DocumentFilter: {
        empty: {
          default: 'There are no documents yet',
        },
        label: 'Document | Documents',
        placeholder: {
          default: 'documents',
        },
        searchPlaceholder: {
          default: 'Search documents',
        },
      },
      DocumentGroupFilter: {
        empty: {
          default: 'There are no document groups yet',
        },
        label: 'Document group | Document groups',
        placeholder: {
          default: 'document groups',
        },
        searchPlaceholder: {
          default: 'Search document groups',
        },
      },
      DocumentNameFilter: {
        label: 'Document name',
      },
      MemoGroupFilter: {
        empty: {
          default: 'There are no memo groups yet',
        },
        label: 'Memo group | Memo groups',
        placeholder: {
          default: 'memo groups',
        },
        searchPlaceholder: {
          default: 'Search memo groups',
        },
      },
      MemoNameFilter: {
        label: 'Memo name',
      },
      openAccessPdf: {
        label: 'Open Access PDF',
      },
      OwnerFilter: {
        empty: {
          default: 'There are no users yet',
        },
        label: 'Owner | Owners',
        placeholder: {
          default: 'users',
        },
        searchPlaceholder: {
          default: 'Search users',
        },
      },
      ProjectNameFilter: {
        label: 'Name',
      },
      ProjectTypeFilter: {
        empty: {
          default: 'There are no types yet',
        },
        label: 'Type',
        placeholder: {
          is: 'project type',
        },
        searchPlaceholder: {
          is: 'Search project types',
        },
      },
      publicationTypes: {
        label: 'Publication types',
        placeholder: {
          default: 'types',
        },
        searchPlaceholder: {
          default: 'Search publication types',
        },
      },
      QuotationFilter: {
        label: 'Content',
      },
      ReportNameFilter: {
        label: 'Name',
      },
      TeamMemberFilter: {
        label: 'Team | Team members',
        placeholder: {
          default: 'users',
        },
        searchPlaceholder: {
          default: 'Search users',
        },
      },
    },
    hide: 'Hide Rules',
    modes: {
      contains: 'contains',
      hasall: 'has all of',
      hasany: 'has any of',
      hasnone: 'has none of',
      hasnothing: 'has none',
      is: 'is exactly',
      notcontain: 'does not contain',
    },
    or: 'or',
    results: 'No results | {count} result | {count} results',
    show: 'Show Rules',
    status: 'No filter rules applied | 1 filter rule applied | {ruleCount} filter rules applied',
    title: 'Filter {entityType}',
    unsaved: 'Unsaved Filters',
  },
  globalSearch: {
    emptyResultLines:
      'Search across documents and document groups to find the information you need. Enter your search above to begin.',
    error:
      'It looks like something went wrong while processing your search. Please try again in a moment. If the problem persists, feel free to contact support.',
    filter: {
      document: {
        count: '0 document | 1 document | {count} documents',
        label: 'Documents',
      },
      documentGroups: {
        count: '0 document group | 1 document group | {count} document groups',
        label: 'Document Groups',
      },
    },
    loading: 'Searching …',
    modalSections: {
      create: 'Create',
      document: 'Document',
      navigation: 'Navigation',
      project: 'Project',
      searchFor: 'Search for “{searchTerm}” in documents',
    },
    noResultLines: 'We couldn’t find any results matching your search terms.',
    search: 'Search…',
    searchInDocuments: 'Search in documents',
  },
  gridList: {
    headers: {
      bibReference: 'Bibliographical references',
      codedWith: 'Coded with',
      codes: 'Codes',
      color: 'Color',
      comment: 'Comment',
      createdAt: 'Created At',
      createdBy: 'Created By',
      documentName: 'Document Name',
      groups: 'Groups',
      images: 'Has Images',
      name: 'Name',
      quotations: 'Quotations',
      updatedAt: 'Updated At',
      updatedBy: 'Updated By',
    },
    renderers: {
      cellEditors: {
        groupCellRenderer: {
          manageGroups: 'Manage Groups',
        },
      },
      colorRenderer: {
        chooseColor: 'Choose a color',
      },
      groupPillRenderer: {
        manageGroups: 'Manage Groups',
      },
    },
  },
  groupPillList: {
    emptyPlaceholder: {
      code: 'This code is not in a group yet.',
      memo: 'This memo is not in a group yet.',
      source: 'This document is not in a group yet.',
    },
  },
  helpscout: {
    statisticsCard: {
      action: {
        allowStatistics: 'Allow statistical data',
      },
      text: 'For a better support experience, allow us to share statistical user data with HelpScout and other third party services. This gives you a personal support history across different ATLAS.ti applications and message channels.',
      title: 'Statistical Data',
    },
  },
  itemDelete: {
    confirm: 'Delete',
    message: 'This will delete 1 selected item. | This will delete {count} selected items.',
    title: 'Confirm Deletion',
  },
  itemDuplicate: {
    confirm: 'Duplicate {entity}',
    title: 'Duplicate {entity}',
  },
  languages: {
    de: 'German',
    en: 'English (US)',
    es: 'Spanish',
  },
  licensing: {
    action: {
      limited: 'You can always use the limited version',
      startTrial: 'Start your trial',
      trial: 'Free Trial',
      upgrade: 'Upgrade Now',
    },
    banner: {
      text: 'Unlock AI features, team collaboration, unlimited codes and more!',
    },
    blockLeaseButton: {
      blockAction: {
        text: 'Reserve license seat for 2 weeks',
      },
      blockTitle: 'Reserve license seat',
      unblockAction: {
        text: '<strong>License seat reserved until {untilDate}</strong><br>Click to unblock license seat',
      },
      unblockTitle: 'Unblock license seat',
    },
    kickoutLeaseCard: {
      text: {
        action: 'Use this computer',
        main: 'If you are reading this, you probably blocked a license seat on a different computer, browser or browser tab.',
        title: 'Your license seat is used on a different computer or browser',
      },
    },
    limitedVersionCard: {
      text: {
        title: 'It’s annoying, we know, but you have reached the boundaries of the limited version',
        trialOrUpgrade:
          'Gain access to all functionalities by starting a <strong>free trial</strong> or upgrading your license.',
        upgrade: 'Gain back access to all functionalities by upgrading your license.',
      },
    },
    noSeatCard: {
      text: {
        main: 'Try again in a few minutes or contact your license holder.',
        title: 'This license is exceeding the maximum user count',
      },
    },
    text: {
      training:
        'If you are in an ATLAS.ti product training, let your trainer know that ATLAS.ti offers special training licenses. Your Trainer can contact ATLAS.ti Support at atlasti.com/support.',
    },
    trialNoticeCard: {
      text: {
        main: 'Consider upgrading to keep access to all functionality.',
        title: 'Your trial has expired | 1 day left in your trial | {count} days left in your trial',
      },
    },
    trialStartupCard: {
      text: {
        main: 'You have <strong>{count} days left in your free trial</strong>. Consider upgrading to keep access to all functionality and future updates.',
        title: 'Pardon the interruption',
      },
    },
    unverifiedCard: {
      text: {
        main: 'Your action did not complete because your license could not be verified. This is usually a temporary problem, and should work after a retry. If you experience this regularly, please contact support.',
        title: 'Your license could not be verified',
      },
    },
    upgradeCard: {
      beta: {
        text: {
          contactUs: 'Contact us',
          main: '{contactUs} to get a discount when purchasing a license.',
          title: 'Thanks for your Support during the ATLAS.ti Web Beta Phase!',
        },
      },
    },
    url: {
      trial: 'https://atlasti.com/free-trial-version/',
    },
  },

  loginExpiredCard: {
    action: 'Go to Login',
    text: {
      main: "Your login session must be refreshed. Changes won't be saved until after you have logged in again.",
      title: 'Your login has expired',
    },
  },
  main: {
    cookieconsent: 'Cookies',
    copyright: '© 2002–{currentYear} – ATLAS.ti Scientific Software Development GmbH',
    footernav:
      '<a href="https://atlasti.com/legal/privacy-policy/" target="_blank">Privacy</a> • <a href="https://atlasti.com/legal/terms-conditions" target="_blank">Terms</a>',
  },
  memo: {
    titlePlaceholder: 'Untitled Memo',
  },
  memoDetail: {
    confirmDelete: {
      buttonText: 'Delete',
      text: 'This Memo will be permanently deleted.',
      title: 'Confirm Memo Deletion',
    },
    contentPlaceholder:
      'Write or paste content here. We will auto-save your progress and keep your documents safe! Tip: Use the native dictation function which is provided by most of the modern operating systems.',
    deleted: {
      icon: '🙄',
      text: 'This memo has been deleted. Sorry.',
      title: 'The memo you are looking for could not be found.',
    },
    editMode: 'Edit Mode',
    save: {
      inProgress: 'Saving …',
      success: 'Memo saved',
    },
    share: 'Share',
    titlePlaceholder: 'Untitled Memo',
  },
  memoDetailCard: {
    bibReference: {
      label: 'Bibliographical references',
      placeholder: 'Add your bibliographical references',
    },
    commentLabel: {
      memo: 'Memo comment',
    },
    commentPlaceholder: {
      memo: 'Leave a comment on this memo',
    },
  },
  memoOverview: {
    deleted: 'No Memos deleted | 1 Memo deleted | {count} Memos deleted',
    duplicated: 'No Memos duplicated | 1 Memo duplicated | {count} Memos duplicated',
    empty: {
      cta: 'Create new memo',
      helpUrl: 'https://atlastihelp.helpscoutdocs.com/article/739-how-to-use-memos-in-atlas-ti',
      icon: '🙈',
      text: 'Create memos to record the results of your analysis, document your progress and reflect on your insights, or use it for your personal notes. {link}',
      title: 'Use memos to document your analysis process.',
    },
    selected: 'No Memos selected | 1 Memo selected | {count} Memos selected',
    title: 'Memo Manager',
  },
  menu: {
    helpAndSupport: 'Help & Support',
    more: 'More',
    projectSettings: 'Project Settings',
    support: 'Support',
    trial: 'Trial has expired | One day left in your trial | {count} days left in your trial',
  },
  mergeCodesCard: {
    comment: {
      label: 'Comment code',
      placeholder: 'Merged from {code1} and {code2}',
    },
    cta: 'Merge {count} codes',
    description: 'You are about to merge {code1} and {code2}.',
    headline: 'Merge {count} codes',
    name: {
      label: 'Name your code',
      placeholder: 'Name of the new code',
    },
    warning: 'Danger Zone. Merging of codes cannot be undone.',
  },
  no: 'No',
  notFound: {
    cta: 'Go to start',
    headline: 'Oops!',
    subline: 'This page does not exist.',
  },
  nothingHere: {
    info: 'Nothing here.',
  },
  notification: {
    accountDeleted: 'Your account has been deleted',
    addedToGroup: 'Added to group {name}',
    chunkLoadError: 'Could not load part of ATLAS.ti Web. Reload page to try again.',
    codeCreated: 'New code created',
    codeRemovedFromQuotation: 'Code removed {name}',
    codesMerged: '{count} codes have been merged',
    entityGroupsUpdated: '{entityType} groups updated',
    entityNameChanged: '{entity} name changed',
    error: 'An error occurred: {code}',
    filterCreated: 'New filter View created',
    filterSavedNew: 'Saved as new View',
    filterUpdated: 'Filter updated',
    generalError: 'An error occurred',
    groupCreated: 'New group created',
    groupDeleted: 'Group {name} deleted',
    groupUpdated: 'Group {name} updated',
    logout: 'You have been successfully logged out.',
    memoCreated: 'New memo created',
    memoMustSync: 'The memo needs to be synced. Until then, editing is disabled.',
    memoNotFound: 'This memo does not exist.',
    offline: 'Internet Connection lost. Changes will not be saved.',
    online: 'Internet connection restored.',
    projectCreated: 'New project created: {name}',
    projectDeleted: 'The project you have been trying to access has been deleted.',
    projectDuplicated: 'Project duplicated: {name}',
    projectNoAccess: 'You do not have the permissions to view this project.',
    projectNotFound: 'This project does not exist.',
    projectOwnershipTransferred: 'Transferred project ownership',
    projectSuccessfullyDeleted: 'Project {name} has been deleted.',
    quotationCreated: 'Quotation created',
    quotationUpdated: 'Quotation updated | {count} quotations updated',
    removedCodeFromGroup: 'Removed code from group {name}',
    removedMemoFromGroup: 'Removed memo from group {name}',
    removedSourceFromGroup: 'Removed document from group {name}',
    reportCreated: 'View created',
    reportDeleted: 'View {name} deleted',
    reportNotFound: 'This view does not exist',
    reportSavedAsNew: 'Saved as View',
    savedConsent: 'Your new preferences will be applied after a page reload',
    serverError: "We couldn't connect to our servers. Sorry. Please try reloading the page to fix the issue.",
    setupInProgress: 'We are setting up ATLAS.ti Web. Hang in there …',
    sourceCreated: 'No new document created | New document created | {count} documents created',
    sourceMustSync: 'The document needs to be synced. Until then, editing and coding is disabled.',
    sourceNotFound: 'This document does not exist.',
    teamMemberRemoved: 'Removed team member from project',
  },
  onboarding: {
    close: 'Close Tour',
    finish: "Let's get started",
    restart: 'Restart Tour',
    steps: {
      0: {
        title: 'Welcome on board!',
      },
      1: {
        content:
          'We help you analyzing your qualitative data and pinpointing the research insights you’re looking for – no matter if you’re dealing with interviews, focus groups, surveys, field notes, or any other content source.',
        title: 'Welcome on board!',
      },
      2: {
        content:
          'With ATLAS.ti, you can say goodbye to endless manual analysis and hello to AI-driven results on autopilot.<br /><br />Select one or more documents, click AI Coding” and try it for yourself.',
        title: 'AI Coding powered by OpenAI',
      },
      3: {
        content:
          'ATLAS.ti Web is built upon a community of users who share their needs and opinions. Our team is constantly improving and developing the app and would love to hear your feedback via live chat. Join us in making ATLAS.ti Web better.',
        title: 'Your opinion counts!',
      },
      4: {
        content:
          'Our <a href="https://atlastihelp.helpscoutdocs.com/collection/666-getting-started" target="_blank">Getting Started Guide</a> supports you with all the knowledge you’ll need to get you started. Our team is here ready to answer your questions. Simply send us an in-app chat message or drop us a line: <a href="mailto:{supportMail}">{supportMail}</a>',
        title: 'Need more guidance?',
      },
      5: {
        content:
          'Invite your team to <b>work collaboratively</b> on projects and <b>create unlimited</b> quotations, codes, documents, Views and more.',
        title: 'Unlock the full potential',
      },
    },
  },
  pagination: {
    of: 'of',
    page: 'Page',
  },
  pdfControls: {
    pageSize: {
      automatic: 'Automatic zoom',
      fit: 'Page size',
      fitToWidth: 'Fit into available width',
      original: 'Original size',
    },
  },
  placeholderUser: '{name} (imported)',
  problemContactSupport: 'If the problem persists, please contact support.',
  project: {
    filter: {
      all: 'All Projects',
      invited: 'Pending Invitations',
      joined: 'Joined Projects',
      owned: 'My Projects',
    },
    loading: {
      title: 'We are loading your data, please hold tight …',
    },
  },
  projectActions: {
    deletePermanently: 'Delete project permanently',
    goToJsonTemplate: 'Go to json template',
    goToJsonTestTemplate: 'Go to json test template',
  },
  projectCard: {
    acceptInvitation: 'Accept',
    invitedBy: 'Invited by {name}',
    inviteTooltip: 'Invite Project Member',
    projectInviteBy: 'Project invite from {name}',
    rejectInvitation: 'Decline',
  },
  projectDropdown: {
    features: 'ATLAS.ti Features',
    featuresUrl: 'https://atlasti.com/feature-comparison/',
    feedback: 'Feedback & Help',
    gettingStarted: 'Restart Tour',
    helpTitle: 'Help &amp; Support',
    security: 'Security',
    securityUrl: 'https://atlastihelp.helpscoutdocs.com/article/775-data-security-and-privacy-in-atlas-ti',
    shortcuts: 'Show Keyboard Shortcuts',
    support: 'Visit Help Center',
    supportUrl: 'https://atlastihelp.helpscoutdocs.com',
  },
  projectImport: {
    action: {
      handleUsers: 'Review',
    },
    colors: {
      title: 'Imported Colors',
    },
    error: {
      codeReference: 'Error code: {code}',
      input: {
        fileSize: 'File exceeds size limit of {limit} MB',
        fileType: 'File type must be {allowedFileTypes}',
      },
      message: 'Import failed – "{name}". {description}',
    },
    fileDrop: {
      text: 'Drop file to upload',
    },
    status: {
      complete: 'Successfully imported – "{name}"',
      progress: 'Importing – "{name}": {detailStatus}',
      progressDetail: {
        importBase: 'base data',
        importComments: 'comments {current/total}',
        importMemos: 'memos {current}/{total}',
        importSources: 'documents {current}/{total}',
        inviteUsers: 'invite users',
      },
      uploading: 'Preparing import – "{name}"',
      usersFound: 'Collaborators found – "{name}"',
    },
    users: {
      action: {
        confirmSkip: 'Skip invitations',
        inviteToProject: 'Invite to project',
        inviteUsers: 'Invite users',
        skip: 'Skip this step',
        submit: 'Continue import with invitations',
      },
      section: {
        withAccount: {
          text: 'Some collaborators are already registered with ATLAS.ti. Invite them below to continue working with them. This step can be done later, too.',
          title: 'Collaborators with ATLAS.ti Account',
        },
        withoutAccount: {
          notice:
            'You can invite them later, but they will not be matched with their placeholders, which means you will have a placeholder AND the real person in your project.',
          text: 'These collaborators do not have an ATLAS.ti Account and will be imported as placeholders to preserve the change history of the project data.',
          title: 'Collaborators without ATLAS.ti Account',
        },
      },
      skipConfirmation: {
        text: 'To preserve the change history of your project, all collaborators found in the project will be imported as placeholders to ATLAS.ti Web. If you want to continue collaborating with them in ATLAS.ti Web, please invite them now.',
        title: 'Sure you want to skip this?',
      },
      title: 'Invite Collaborators',
    },
  },
  projectOverview: {
    createProject: {
      description: 'Create a blank ATLAS.ti Web Project.',
      title: 'Start from scratch',
    },
    import: {
      description:
        'This feature is currently in BETA as not all of the ATLAS.ti Desktop features are supported. Upload size is limited to {maxSizeMb}.',
      featureComparison: 'Feature Comparison',
    },
    projects: 'My Projects',
    title: 'Projects',
  },
  projectSettings: {
    action: {
      downloadImportFile: 'Download import file',
      export: 'Export ({version})',
      exportWithoutDownload: 'Export ({version}) without download',
      exportWithoutValidation: 'Export ({version}) without validation',
    },
    deleteProject: 'Delete project',
    deleteProjectCta: 'Delete project',
    deleteProjectDescription:
      'This action will delete your project, including all documents, quotations and the love you put in it. Don’t say we didn’t warn you 😅',
    deleteProjectLabel: 'Project name',
    deleteProjectPlaceholder: 'Enter the project name to delete it',
    error: {
      projectDelete: 'The entered project name is not correct.',
      projectName: 'Please enter a project name.',
    },
    exportProject: 'Export Project',
    exportProjectDescription:
      'Did you know? <strong>ATLAS.ti is available for all major platforms</strong> including Windows and Mac OS. Export your Web project and take advantage of the full ATLAS.ti power. (ATLAS.ti Desktop Versions 8.3. and higher)',
    exportProjectError: {
      codeReference: 'Error code: {code}.',
      documentReference: 'Failed in document [{name}].',
      message: 'The project could not be exported.',
    },
    headline: 'Manage project: {name}',
    projectButton: {
      default: 'Update Project',
      error: 'Could not be updated',
      success: 'Project updated',
    },
    projectExportButton: {
      default: 'Export for ATLAS.ti Desktop',
      error: 'Could not export',
      success: 'Project export file created',
    },
    projectExportXlsxButton: {
      default: 'Export for Excel',
      error: 'Could not export',
      success: 'Excel export created',
    },
    projectInformation: 'Project information',
    projectNameLabel: 'Project Name',
    projectNamePlaceholder: 'Project Name',
    projectSettings: 'Project settings',
    support: {
      checkboxLabel:
        'Allow ATLAS.ti customer support agents access to this project. You can revoke access at any time.',
      headline: 'Customer Support Access',
    },
  },
  prop: {
    created: 'Created',
    email: 'E-Mail | E-Mails',
    firstname: 'First name | First names',
    lastname: 'Last name | Last names',
    occurrence: 'Frequency in project',
    updated: 'Updated',
  },
  quotation: {
    titlePlaceholder: 'Untitled Quotation',
  },
  quotationDetail: {
    comment: {
      label: 'Comment',
      placeholder: 'Add comment to quotation',
    },
  },
  quotationOverview: {
    barChart: {
      emptyFilter: {
        change: 'Change filter rules',
        content: "Couldn't find any results with this filter query. {change} or {reset}.",
        reset: 'reset rules',
        text: 'Try another rule or',
        title: "Couldn't find any results",
      },
    },
    chartSelector: {
      chartType: 'Chart Type',
      position: 'Position',
      showChart: 'Show Chart',
    },
    createReport: 'Save as View',
    deleted: 'No Quotation deleted | 1 Quotation deleted | {count} Quotations deleted',
    download: 'Download Quotations as Excel',
    downloadFiltered: 'Download filtered quotations as Excel',
    empty: {
      helpUrl: 'https://atlastihelp.helpscoutdocs.com/article/769-what-is-the-quotation-manager',
      icon: '😱',
      text: 'The quotation manager gives you a cross-document overview of your quotations. Filter quotations to learn about different aspects and save them as Views for faster access. {link}',
      title: 'Quote relevant text passages in your documents to see them listed here.',
    },
    selected: 'No quotation selected | 1 quotation selected | {count} quotations selected',
    showAllCodes: 'Show all Codes',
    title: 'Quotation Manager',
  },
  report: {
    actions: {
      createReport: 'New View',
      deleteReport: 'Delete View',
      discard: 'Discard changes',
      editReport: 'Edit View',
      saveAsNewReport: 'Save as new View',
      saveAsReport: 'Save as View',
      saveReport: 'Save View',
      toggleChart: 'Chart',
    },
    codeCoOccurrence: {
      addColumn: 'Add Column:',
      addRow: 'Add Row:',
      codesNeeded: '(must create at least two codes)',
      create: 'Code Co-Occurrence (Beta)',
      createFromCodeSideDetail: 'Create Code Co-Occurrence View',
      empty: {
        helpUrl: 'https://atlastihelp.helpscoutdocs.com/article/593-what-is-the-code-co-occurrence-view',
        table: {
          text: 'Select at least one code relation to show a results table.',
          title: 'No data to display.',
        },
        text: 'Add at least one row and one column to display a Code Co-Occurrence Table',
        title: 'More Data needed',
      },
      info: {
        description:
          "The Code-Cooccurrence Table showcases which codes frequently appear together in your dataset.<br /><br />For example, if you're studying exercise habits and have codes like 'Morning' and 'Running', a high cooccurrence might suggest many participants prefer running in the morning.<br />By examining these patterns, you can discover underlying themes and relationships, helping you derive deeper insights from your data.",
        title: 'Code Co-Occurrence View',
      },
      removeEmpty: 'Remove empty rows/columns',
    },
    codeDistribution: {
      create: 'Code Distribution',
      title: 'Code Distribution',
    },
    codeDocumentTable: {
      addColumn: 'Add Column:',
      addRow: 'Add Row:',
      create: 'Code-Document Table',
      empty: {
        helpUrl: 'https://atlastihelp.helpscoutdocs.com/article/591-what-is-the-code-document-table-tool',
        table: {
          text: 'Select at least one code-article relation to show result table.',
          title: 'No data to display.',
        },
        text: 'Add at least one row and one column to display a Code-Document Relation',
        title: 'More Data needed',
      },
    },
    comment: {
      label: 'Comment',
      placeholder: 'Describe what you observe …',
    },
    conceptAnalysis: {
      badInput: 'The selection contains invalid or deleted entities. Please refresh the page and try again.',
      cannotLoad: 'Could not load concepts cloud',
      clearSelection: 'Clear',
      create: 'Concepts (Beta)',
      empty: {
        helpUrl: 'https://atlastihelp.helpscoutdocs.com/article/754-how-to-use-the-concepts-tool-in-atlas-ti',
        text: 'Select at least one entity to analyze the concepts and display a word cloud',
        title: 'More data needed',
      },
      error: 'Could not extract concepts. Error code: {code}',
      loading: 'Generating concepts cloud …',
      noItems: 'No concepts found',
      selectAll: 'Select all',
      selectEntities: 'Select entities to process: ',
    },
    emptyFilter: {
      change: 'Change filter rules',
      content: "Couldn't find any results with this filter query. {change} or {reset}.",
      reset: 'Reset rules',
      text: 'Try another rule or',
      title: "Couldn't find any results",
    },
    name: {
      label: 'View name',
      placeholder: 'My View',
    },
    overviewTitle: 'Views',
    reportList: {
      savedReports: 'Saved Views',
    },
    titlePlaceholder: {
      barChart: 'Untitled Code-Distribution View',
      codeCoOccurrence: 'Untitled Code Co-Occurrence View',
      codeDocumentTable: 'Untitled Code-Document View',
      conceptAnalysis: 'Untitled Concepts View',
    },
    types: {
      barChart: 'Code-Distribution View',
      codeCoOccurrence: 'Code Co-Occurrence View',
      codeDocumentTable: 'Code-Document View',
      conceptAnalysis: 'Concepts View',
    },
    visualization: {
      barChart: {
        title: 'Bar Chart (vertical)',
      },
      donutChart: {
        title: 'Donut Chart - Beta',
      },
      genericTitle: 'Chart',
      horizontalBarChart: {
        title: 'Bar Chart (horizontal)',
      },
      sankeyChart: {
        title: 'Sankey Chart - Beta',
      },
      table: {
        title: 'Table',
      },
      treeMap: {
        title: 'Treemap - Beta',
      },
    },
  },
  reportDetail: {
    card: {
      commentPlaceholder: 'What are you observing?',
    },
    confirmDelete: {
      buttonText: 'Confirm View Deletion',
      text: 'This View will be permanently deleted.',
      title: 'Confirm View Deletion',
    },
    deleted: {
      icon: '🙄',
      text: 'This view has been deleted. Sorry.',
      title: 'The view you are looking for could not be found.',
    },
  },
  reportOverview: {
    deleted: 'No View deleted | 1 View deleted | {count} Views deleted',
    duplicated: 'No View duplicated | 1 View duplicated | {count} Views duplicated',
    empty: {
      helpUrl: 'https://atlastihelp.helpscoutdocs.com/category/756-visualization-and-analysis',
      text: 'Start quoting relevant text passages in your documents and show the distribution of codes with a Bar Chart View or see their relation in a Code-Document Table. {link}',
      title: 'Make your research visible',
    },
    selected: 'No View selected | 1 View selected | {count} Views selected',
    title: 'Views',
  },
  scholar: {
    action: {
      cite: 'Cite',
      importPdf: 'Import PDF',
      loadMoreResults: 'Load more results',
      openDetail: 'Open detail',
      tryPaperSearch: 'Try Paper Search',
      viewInProject: 'Go to PDF',
      viewSource: 'View source',
    },
    citationCard: {
      copied: 'Copied to clipboard',
      error: 'Could not load bibliography information: {code}',
    },
    documentUnavailable:
      'We were unable to download the PDF because the host has restricted downloads. However, the document is still accessible on the source website.',
    feedback: 'Have Feedback?',
    importingDocument: 'Importing PDF “{name}”',
    paperSearch: {
      aiSummary: {
        action: {
          copy: 'Copy',
          newQuestion: 'Enter a research question',
          saveAsMemo: 'Save as memo',
          seeResults: 'See results',
        },
        conclusion: 'Conclusion',
        copied: 'Copied',
        empty: {
          INVALID_RESEARCH_QUESTION: {
            text: 'Summaries are generated based on research questions. Please try again by entering a specific research question to see relevant paper summaries.',
            title: 'Try asking a research question',
          },
          NOT_ENOUGH_DATA: {
            suggestion:
              'To get better results, consider broadening your research question or exploring related topics.',
            text: "Unfortunately, there aren't enough relevant papers available to create a meaningful summary. This can happen if the topic is very specific, the research area is new or emerging, or there's limited published work on the subject.",
            title: 'Not enough papers found to summarize',
          },
        },
        keyInsights: 'Key Insights',
        notice: {
          text: 'Our data sources provide the key papers for this feature. It strives to represent up-to-date scientific understanding, but may not encompass every nuance of the topic. Please note that AI-generated summaries may contain inaccuracies or hallucinations. Always review the original abstracts and papers to verify information. In some cases, abstracts may be unavailable due to limitations in our data source. We recommend using these summaries as a starting point for further investigation rather than as definitive information.',
          title: 'Be aware!',
        },
      },
      detail: {
        citationCount: 'No Citations | 1 Citation | {count} Citations',
        publishedAt: 'Published {date}',
        publishedAtIn: 'Published {date} in {venue}',
        publishedIn: 'Published in {venue}',
        referenceCount: 'No References | 1 Reference | {count} References',
      },
      export: {
        action: 'Export results',
        types: {
          bibtex: 'BibTex File',
          csv: 'CSV File',
          excel: 'Excel File',
          ris: 'RIS File',
          xml: 'XML File',
        },
      },
      headers: {
        abstract: {
          name: 'Abstract',
        },
        authors: {
          name: 'Authors',
        },
        citations: {
          name: 'Citations',
          tooltip:
            'Total number of citations found for this resource. More citations generally indicate greater relevance and influence.',
        },
        DOI: {
          name: 'DOI',
          tooltip: 'A DOI (Digital Object Identifier) is a unique code for academic resources',
        },
        fieldsOfStudy: {
          name: 'Fields of study',
        },
        influentialCitations: {
          name: 'Influential citations',
          tooltip:
            'Citations that notably affect the works citing them. Identified by a machine-learning model analyzing citation numbers and context. Powered by Semantic Scholar.',
        },
        isOpenAccess: {
          name: 'Is open access',
        },
        publicationDate: {
          name: 'Publication date',
        },
        publicationTypes: {
          name: 'Publication types',
        },
        PubMed: {
          name: 'PubMed',
        },
        references: {
          name: 'References',
          tooltip:
            'Total number of papers referenced by this resource. A higher number of references can indicate thorough research and strengthen the credibility of the paper.',
        },
        title: {
          name: 'Title',
        },
        tldr: {
          name: 'TL;DR',
          tooltip: 'Auto-generated short summary of the resource from the SciTLDR model',
        },
        venue: {
          name: 'Publication',
        },
      },
      inputPlaceholder: 'Enter research question',
      loading: 'Loading …',
      noResults: {
        headline: 'We couldn’t find any results matching your search term.',
        newSearch: 'New search',
      },
      notProvided: 'Not provided',
      splash: {
        description:
          'Efficiently find and import relevant scientific resources into your project with our streamlined search. Utilize filters to narrow down options and sort by relevance.',
        powered: 'Powered by Semantic Scholar',
        subheadline: 'Access and explore over 200 million scientific resources.',
        title: 'ATLAS.ti Paper Search',
      },
      title: 'Paper Search',
      yearPicker: {
        fromInput: {
          label: 'From',
          placeholder: 'Start year',
        },
        openerButton: 'Year',
        reset: 'Reset',
        select: {
          all: 'All Time',
          last3: 'Last 3 years',
          last5: 'Last 5 years',
        },
        title: 'Filter',
        toInput: {
          label: 'To',
          placeholder: 'End year',
        },
      },
    },
  },
  selectQuotationsCard: {
    action: 'Add 1 quotation | Add {count} quotations',
    emptyState: {
      action: 'Open View',
      text: 'This View contains no quotations or the filter rules yield no results.',
      title: 'No quotation found',
    },
    title: 'Select quotations',
  },
  sentimentOptions: {
    close: 'Cancel',
    codes: {
      negative: 'Negative Sentiment',
      neutral: 'Neutral Sentiment',
      positive: 'Positive Sentiment',
    },
    cta: 'No Results | Code Result | Code {results} Results',
    ctaInfo: 'This might take a while depending on the scope of the document.',
    description:
      'Perform a sentiment analysis on this document to gain insights over positive, negative or neutral connotated text passages. This process may take a while depending on the scope of your document.',
    error: {
      general: 'Could not analyze document. Error code: {code}',
    },
    extent: {
      label: 'Analyze Text based on:',
      paragraph: 'Paragraph',
      sentence: 'Sentence',
    },
    loading: 'Analyzing document …',
    sentiment: {
      negative: 'negative',
      neutral: 'neutral',
      positive: 'positive',
    },
    start: 'Start Sentiment Analysis',
    title: 'Sentiment analysis (currently in beta)',
  },
  shortcutModal: {
    createcode: 'Create new code',
    creatememo: 'Create new memo',
    createproject: 'Create new project',
    createsource: 'Create new document',
    creation: 'creation',
    gocodes: 'Go to codes',
    gomemos: 'Go to memos',
    goprojects: 'Go to projects',
    goquotations: 'Go to quotations',
    goreports: 'Go to views',
    gosettings: 'Go to settings',
    gosources: 'Go to documents',
    navbartoggle: 'Toggle navigation',
    navigation: 'navigation',
    title: 'Useful Keyboard Shortcuts',
    userlogout: 'Logout',
  },
  sort: {
    codeCount: 'Number of codes',
    codeName: 'Name',
    color: 'Color',
    comment: 'Comment',
    createdAt: 'Creation Date',
    memoName: 'Name',
    name: 'Name',
    occurrence: 'Frequency',
    quotationCount: 'Number of quotations',
    quotationName: 'Name',
    quotationSourceName: 'Document name',
    sourceName: 'Name',
    updatedAt: 'Last Update',
  },
  source: {
    aiCoding: 'AI Coding (+ Intention)',
    conversationalAi: 'Conversational AI',
    titlePlaceholder: 'Untitled Document',
  },
  sourceDetail: {
    action: {
      goToNextQuotation: 'Go to next quotation',
      goToPreviousQuotation: 'Go to previous quotation',
      resetWidth: 'Reset document width',
    },
    analyze: 'Analyze',
    avatars: 'Author Avatars',
    batchQuote: {
      create: 'Create quotations',
      created: 'No quotations created | 1 quotation created | {count} quotations created',
      error: 'Could not create quotations',
      fromSelection: 'Create no quotation | Create quotation | Create quotations',
    },
    codingMode: 'Coding mode',
    columnWidth: 'Narrow Code Columns',
    confirmDelete: {
      buttonText: 'Delete',
      text: 'This document will be permanently deleted.',
      title: 'Confirm Document Deletion',
    },
    contentPlaceholder: 'Write or paste content here. We will auto-save your progress and keep your documents safe!',
    conversationalAi: 'Conversational AI',
    deleted: {
      icon: '🙄',
      text: 'This document has been deleted. Sorry.',
      title: 'The document you are looking for could not be found.',
    },
    editMode: 'Edit mode',
    layout: 'Layout',
    save: {
      inProgress: 'Saving …',
      success: 'Document saved',
    },
    sentimentAnalysis: 'Sentiment Analysis',
    share: 'Share',
    showQuotations: 'Show quotations in manager',
    titlePlaceholder: 'Untitled document',
  },
  sourceDetailCard: {
    bibReference: {
      label: 'Bibliographical references',
      placeholder: 'Add your bibliographical references',
    },
    commentLabel: {
      document: 'Document comment',
    },
    commentPlaceholder: {
      document: 'Leave a comment on this document',
    },
  },
  sourceDetailPdf: {
    action: {
      openPdf: 'Open PDF in new tab',
    },
    loadError: 'PDF document could not be loaded. Please try again later, or contact support.',
    pageTextLoadError: 'The text for this page could not be loaded. Please contact support.',
  },
  sourceOverview: {
    deleted: 'No Document deleted | 1 Document deleted | {count} Documents deleted',
    duplicated: 'No Document duplicated | 1 Document duplicated | {count} Documents duplicated',
    duplicateOptions: {
      keepCodes: {
        description: 'Select if you want to copy codes and quotations to the duplicated document.',
        title: 'Keep codes and quotations',
      },
    },
    empty: {
      cta: 'Create document',
      helpUrl: 'https://atlastihelp.helpscoutdocs.com/article/770-what-is-the-document-manager',
      icon: '😅',
      text: 'You can either upload Word- and PDF-files or create new documents to start your analysis. {link}',
      title: 'Add documents to start analyzing.',
    },
    fileDropInfo: 'Drop files to upload',
    fileDropPdfHandler: {
      pdfFiles: 'No file | 1 PDF file | {filesNumber} PDF files',
      title: 'Upload {pdfFiles}',
      titleMultiple: 'Upload {pdfFiles} and {wordFiles}',
      wordFiles: 'No file | 1 Word file | {filesNumber} Word files',
    },
    selected: 'No Document selected | 1 Document selected | {count} Documents selected',
    title: 'Document Manager',
  },
  sourceSearch: {
    actions: {
      codeParagraphs: 'Quote nothing | Quote 1 paragraph | Quote {count} paragraphs',
      codeResults: 'Quote nothing | Quote 1 result | Quote {count} results',
      codeSentences: 'Quote nothing | Quote 1 sentence | Quote {count} sentences',
      quoteParagraphs: 'Quote paragraphs',
      quoteSentences: 'Quote sentences',
      quoteWords: 'Quote exact match',
      search: 'Hit Enter to search',
    },
    allSynonymResultsAdded: 'You have added all the matching terms to your search.',
    currentResult: '{count} of {total}',
    noSearchResults: 'No results for your search',
    noSynonymResults:
      'We couldn’t find matching terms for your search term. Note: The synonym search is supporting English only.',
    searchingSynonyms: 'Looking for terms matching your search – Hang in there …',
    searchPlaceholder: {
      document: 'Search in document',
      memo: 'Search in memo',
    },
    synonymResults: 'Terms matching your search',
  },
  team: {
    invitation: {
      accepted: 'You have joined a new project',
      card: {
        confirmRemove: '👋 Yes, revoke this invitation',
        remove: 'Do you really want to revoke this invitation?',
        resend: 'Resend Invitation',
        sent: 'Invitation sent',
      },
      goto: 'Go to project',
      invitationInvalid: 'The invitation to this project is invalid.',
      invited: 'New member has been invited',
      overlay: {
        cta: 'Send invitation',
        email: {
          error: {
            alreadyInvited: 'This e-mail address is already part of the project.',
            invalid: 'This looks like an invalid e-mail address.',
          },
          label: 'E-Mail',
          placeholder: 'E-Mail address of your new team mate',
        },
        error: 'Invitation could not be sent.',
        firstname: {
          label: 'First Name (optional)',
          placeholder: 'First name',
        },
        headline: 'Invite Project Member',
        lastname: {
          label: 'Last Name (optional)',
          placeholder: 'Last Name',
        },
        message: {
          label: 'Personal Message (optional)',
          placeholder: 'Your message here',
          value:
            'Hi, I am using ATLAS.ti Web to work on a project called "{projectname}" and would like you to join me.',
        },
      },
      projectNotFound: 'The project you were invited to could not be found. It probably has been deleted.',
      rejected: 'You rejected the invitation.',
    },
    leftProject: 'You have left the project',
    member: {
      card: {
        confirmLeaveProject: '👋 Yes, leave project',
        deleteConfirm: '👋 Yes, remove {name}',
        deleteInfo: '{name} will no longer have access to this project',
        deleteTeamMember: 'Remove from project',
        isMember: 'Project Member',
        isOwner: 'Project Owner',
        isSelf: "It's you!",
        lastActive: 'Last active',
        leaveProject: 'Leave project',
        leaveProjectInfo: 'You will no longer have access to this project',
        transferOwnership: {
          action: 'Make project owner',
          confirm: 'Yes, make {name} the owner',
          info: "{name} will be project owner, you will become project member. You won't be able to delete the project, or administrate project members.",
        },
      },
    },
    settings: {
      adminIntro: 'Feeling lonely? Invite your team mates and work on projects collaboratively.',
      headline: 'Your Team',
      inviteTeamMember: 'Add new project member',
      leftMembers: 'People who left the Project',
      memberIntro: '{name} invited you to work on a project in ATLAS.ti Web.',
      pendingInvitations: 'Pending Invitations',
    },
  },
  time: {
    lastEdited: 'Last edited',
  },
  title: {
    codeOverview: "{projectname} {'|'} Code Manager",
    memoDetail: "{sourcename} {'|'} Memo",
    memoOverview: "{projectname} {'|'} Memo Manager",
    paperSearch: "{name} {'|'} Paper Search",
    projectOverview: "ATLAS.ti {'|'} Project Manager",
    projectSettings: "{projectname} {'|'} Settings",
    quotationOverview: "{projectname} {'|'} Quotation Manager",
    reportDetail: "{reportname} {'|'} View",
    reportOverview: "{projectname} {'|'} Views",
    sourceDetail: "{sourcename} {'|'} Document",
    sourceOverview: "{projectname} {'|'} Document Manager",
    userSettings: "ATLAS.ti {'|'} Account settings",
  },
  tools: 'Tools',
  userActivityCombined: {
    codes: 'Codes',
    documentContains: 'Contains',
    quotations: 'Quotations',
  },
  userImageEditCard: {
    cta: 'Save profile picture',
    title: 'Crop profile picture',
  },
  userSettings: {
    deleteAccount: 'Permanently delete my ATLAS.ti Web account',
    deleteAccountButton: 'Delete my account',
    deleteAccountCheckbox: 'Yes, I want to permanently delete my account and all my associated data.',
    deleteAccountCheckbox2: 'Yes, I am really sure that I want to delete my account.',
    deleteAccountDescription:
      "If you do not want to use ATLAS.ti Web anymore you are free to delete all your associated data in a blink of an eye. If you don't like ATLAS.ti Web, tell us what we can do better. Note: Deleting all your data might take up to 10 days. Deletions cannot be restored.",
    image: {
      choose: 'Upload profile picture',
      delete: 'Delete profile picture',
      description: 'Recommended size: 200 x 200, JPG/PNG, Maximum file size: {imageMaxFileSize}',
      error: {
        fileTooBig: 'This image exceeds the maximum file size of {imageMaxFileSize}.',
        fileUnsupported: 'This file type is not supported.',
        uploadFailure: 'Oops. Failed to upload the profile picture.',
      },
      headline: 'Profile picture',
      uploadInProgress: 'Uploading…',
    },
    language: 'Select the language in which ATLAS.ti Web will be displayed',
    languageDescription: 'Can’t find your language? Please drop us a line at {link}',
    title: 'ATLAS.ti Web Settings',
  },
  yes: 'Yes',
} as const;

export type Messages = ConvertStringLiteralsToStringDeep<typeof messages>;
